import { useState } from 'react';
import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { card, cardPad } from '../../lib/ui';

// The supplier under review (matches the first Pending Review row).
const SUPPLIER = {
  id: 'SUP-008', name: 'Orion Marine Insurance', category: 'Insurance',
  contact: 'ops@orion-marine.co', phone: '+66 2 555 0180',
  taxId: '0105539004521', registered: '2026-06-18', registry: 'Pending',
  docs: [
    { name: 'Business Registration Certificate', ok: true },
    { name: 'VAT Registration (Phor.Phor.20)', ok: true },
    { name: 'Insurance License — OIC', ok: true },
    { name: 'Financial Statements (2 yrs)', ok: false },
  ],
};

const TIERS = [
  { key: 'A', label: 'Tier A', desc: 'Strategic — full spend, priority invitations' },
  { key: 'B', label: 'Tier B', desc: 'Approved — standard RFQ participation' },
  { key: 'C', label: 'Tier C', desc: 'Provisional — capped value, monitored' },
];

const REJECT_REASONS = ['Incomplete documentation', 'Failed registry verification', 'Adverse financial standing', 'Duplicate / already registered', 'Other'];

const DOC_TH: Record<string, string> = {
  'Business Registration Certificate': 'หนังสือรับรองการจดทะเบียนบริษัท',
  'VAT Registration (Phor.Phor.20)': 'ทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20)',
  'Insurance License — OIC': 'ใบอนุญาตประกันภัย — คปภ.',
  'Financial Statements (2 yrs)': 'งบการเงิน (2 ปี)',
};
const TIER_DESC_TH: Record<string, string> = {
  A: 'เชิงกลยุทธ์ — ใช้จ่ายเต็มที่ เชิญเข้าร่วมก่อน',
  B: 'อนุมัติแล้ว — เข้าร่วม RFQ ตามปกติ',
  C: 'ชั่วคราว — จำกัดมูลค่า ติดตามดูแล',
};
const REASON_TH: Record<string, string> = {
  'Incomplete documentation': 'เอกสารไม่ครบถ้วน',
  'Failed registry verification': 'การตรวจสอบทะเบียนไม่ผ่าน',
  'Adverse financial standing': 'สถานะการเงินไม่ดี',
  'Duplicate / already registered': 'ซ้ำ / ลงทะเบียนแล้ว',
  'Other': 'อื่นๆ',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--color-fg-2)' } as const;

export default function ReviewDetail() {
  const { go, L } = useApp();
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [tier, setTier] = useState('');
  const [reason, setReason] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [done, setDone] = useState<'approved' | 'rejected' | null>(null);

  const tReason = (r: string) => L(r, REASON_TH[r] ?? r);
  const canApprove = decision === 'approve' && tier !== '';
  const canReject = decision === 'reject' && reason !== '';

  if (done) {
    const ok = done === 'approved';
    return (
      <div style={{ ...cardPad(40), maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: 999, background: ok ? 'var(--color-success-bg)' : 'var(--color-error-bg)', color: ok ? 'var(--color-success)' : 'var(--color-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 26 }}><Icon name={ok ? 'check' : 'x'} /></div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{ok ? L(`Supplier Approved — Tier ${tier}`, `อนุมัติซัพพลายเออร์แล้ว — ระดับ ${tier}`) : L('Supplier Rejected', 'ปฏิเสธซัพพลายเออร์แล้ว')}</div>
        <div style={{ fontSize: 13, color: 'var(--color-fg-3)', maxWidth: 400, margin: '0 auto 22px' }}>
          {ok
            ? L(`${SUPPLIER.name} (${SUPPLIER.id}) has been added to the Approved Supplier List and notified.`, `${SUPPLIER.name} (${SUPPLIER.id}) ถูกเพิ่มเข้าในรายชื่อผู้ขายที่อนุมัติและได้รับการแจ้งเตือนแล้ว`)
            : L(`${SUPPLIER.name} (${SUPPLIER.id}) has been rejected. Reason: ${reason}. The applicant has been notified.`, `${SUPPLIER.name} (${SUPPLIER.id}) ถูกปฏิเสธ เหตุผล: ${tReason(reason)} ผู้สมัครได้รับการแจ้งเตือนแล้ว`)}
        </div>
        <button onClick={() => go('review')} className="hv-primary" style={{ padding: '10px 20px', background: 'var(--color-primary)', color: '#fff', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14 }}>{L('Back to Supplier Review', 'กลับไปยังการตรวจสอบซัพพลายเออร์')}</button>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => go('review')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--color-fg-2)', marginBottom: 16 }}><Icon name="arrow-left" />{L('Back to Supplier Review', 'กลับไปยังการตรวจสอบซัพพลายเออร์')}</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        {/* Registration details */}
        <div style={{ ...card, padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <span style={{ width: 40, height: 40, borderRadius: 999, background: 'var(--color-ink-50)', color: 'var(--color-fg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>{SUPPLIER.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>
            <div>
              <div style={{ fontWeight: 700 }}>{SUPPLIER.name}</div>
              <div style={{ fontSize: 12, color: 'var(--color-fg-3)', fontFamily: 'var(--font-mono)' }}>{SUPPLIER.id} · {L(SUPPLIER.category, 'ประกันภัย')}</div>
            </div>
          </div>
          {([
            [L('Contact', 'ผู้ติดต่อ'), SUPPLIER.contact],
            [L('Phone', 'โทรศัพท์'), SUPPLIER.phone],
            [L('Tax ID', 'เลขประจำตัวผู้เสียภาษี'), SUPPLIER.taxId],
            [L('Registered', 'ลงทะเบียนแล้ว'), SUPPLIER.registered],
          ] as [string, string][]).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--color-border)', fontSize: 13 }}>
              <span style={{ color: 'var(--color-fg-3)' }}>{k}</span>
              <span style={{ fontWeight: 600 }}>{v}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-fg-3)', margin: '18px 0 10px' }}>{L('Documents', 'เอกสาร')}</div>
          {SUPPLIER.docs.map((d) => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', fontSize: 13 }}>
              <Icon name={d.ok ? 'check-circle' : 'alert-triangle'} style={{ color: d.ok ? 'var(--color-success)' : 'var(--color-warning)', flex: 'none' }} />
              <span style={{ flex: 1, color: 'var(--color-fg-2)' }}>{L(d.name, DOC_TH[d.name] ?? d.name)}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: d.ok ? 'var(--color-success)' : 'var(--color-warning)' }}>{d.ok ? L('Received', 'ได้รับแล้ว') : L('Missing', 'ขาดหาย')}</span>
            </div>
          ))}
        </div>

        {/* Decision */}
        <div style={{ ...card, padding: 22 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{L('Decision', 'การตัดสิน')}</div>
          <div style={{ fontSize: 12, color: 'var(--color-fg-3)', marginBottom: 16 }}>{L('Approve the supplier onto the ASL with a tier, or reject with a reason.', 'อนุมัติซัพพลายเออร์เข้าสู่ ASL พร้อมกำหนดระดับ หรือปฏิเสธพร้อมระบุเหตุผล')}</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
            <button onClick={() => setDecision('approve')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 0', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, border: `2px solid ${decision === 'approve' ? 'var(--color-success)' : 'var(--color-border)'}`, background: decision === 'approve' ? 'var(--color-success-bg)' : 'var(--color-white)', color: decision === 'approve' ? 'var(--color-success-ink)' : 'var(--color-fg-2)' }}><Icon name="check" />{L('Approve', 'อนุมัติ')}</button>
            <button onClick={() => setDecision('reject')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 0', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, border: `2px solid ${decision === 'reject' ? 'var(--color-error)' : 'var(--color-border)'}`, background: decision === 'reject' ? 'var(--color-error-bg)' : 'var(--color-white)', color: decision === 'reject' ? 'var(--color-error-ink)' : 'var(--color-fg-2)' }}><Icon name="x" />{L('Reject', 'ปฏิเสธ')}</button>
          </div>

          {decision === 'approve' && (
            <div className="aibid-fade">
              <label style={labelStyle}>{L('Assign Tier', 'กำหนดระดับ')}</label>
              {TIERS.map((tr) => (
                <button key={tr.key} onClick={() => setTier(tr.key)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 8, border: `2px solid ${tier === tr.key ? 'var(--color-primary)' : 'var(--color-border)'}`, background: tier === tr.key ? 'var(--color-primary-light)' : 'var(--color-white)' }}>
                  <span style={{ width: 30, height: 30, flex: 'none', borderRadius: 999, background: tier === tr.key ? 'var(--color-primary)' : 'var(--color-ink-50)', color: tier === tr.key ? '#fff' : 'var(--color-fg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{tr.key}</span>
                  <span style={{ flex: 1 }}><span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{L(tr.label, 'ระดับ ' + tr.key)}</span><span style={{ fontSize: 11, color: 'var(--color-fg-3)' }}>{L(tr.desc, TIER_DESC_TH[tr.key] ?? tr.desc)}</span></span>
                  <Icon name="check" style={{ color: 'var(--color-primary)', opacity: tier === tr.key ? 1 : 0 }} />
                </button>
              ))}
            </div>
          )}

          {decision === 'reject' && (
            <div className="aibid-fade">
              <label style={labelStyle}>{L('Rejection Reason', 'เหตุผลการปฏิเสธ')}</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-white)', fontSize: 14, marginBottom: 12 }}>
                <option value="">{L('Select a reason…', 'เลือกเหตุผล…')}</option>
                {REJECT_REASONS.map((r) => <option key={r} value={r}>{tReason(r)}</option>)}
              </select>
              <textarea value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} placeholder={L('Add a note to the applicant (optional)…', 'เพิ่มหมายเหตุถึงผู้สมัคร (ไม่บังคับ)…')} style={{ width: '100%', minHeight: 90, padding: 11, border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-white)', fontSize: 14, resize: 'vertical' }} />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button onClick={() => go('review')} className="hv-ink" style={{ padding: '10px 18px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14, background: 'var(--color-white)', color: 'var(--color-fg-1)' }}>{L('Cancel', 'ยกเลิก')}</button>
            {decision === 'reject' ? (
              <button onClick={() => canReject && setDone('rejected')} disabled={!canReject} className={canReject ? 'hv-danger' : undefined} style={{ padding: '10px 20px', background: canReject ? 'var(--color-error)' : 'var(--color-ink-100)', color: '#fff', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14, cursor: canReject ? 'pointer' : 'not-allowed' }}>{L('Confirm Rejection', 'ยืนยันการปฏิเสธ')}</button>
            ) : (
              <button onClick={() => canApprove && setDone('approved')} disabled={!canApprove} className={canApprove ? 'hv-primary' : undefined} style={{ padding: '10px 20px', background: canApprove ? 'var(--color-primary)' : 'var(--color-ink-100)', color: '#fff', borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: 14, cursor: canApprove ? 'pointer' : 'not-allowed' }}>{L('Confirm Approval', 'ยืนยันการอนุมัติ')}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
